'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTenantStore } from '@/stores/tenant-store';

const DISMISS_KEY = 'dgr_app_banner_dismissed_v1';
const APP_STORE_URL = 'https://apps.apple.com/us/app/distinguished-gentlemans-ride/id6475795204';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.gentlemansride.dgr';

type Platform = 'ios' | 'android' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function AppInstallBanner() {
  const t = useTranslations('appBanner');
  const tenantSlug = useTenantStore((s) => s.slug);
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<Platform>('other');
  const [entered, setEntered] = useState(false);

  // Never overlay the payment page — the fixed banner covers the card form on
  // small screens.
  const onCheckout = pathname?.includes('/donate/checkout') ?? false;

  useEffect(() => {
    if (tenantSlug !== 'dgr') return;
    if (isStandalone()) return;
    if (typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_KEY)) return;
    const detected = detectPlatform();
    if (detected === 'other') return;
    setPlatform(detected);
    setVisible(true);
    const id = window.setTimeout(() => setEntered(true), 600);
    return () => window.clearTimeout(id);
  }, [tenantSlug]);

  if (!visible || onCheckout) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // localStorage unavailable; dismissal is session-only
    }
    setEntered(false);
    window.setTimeout(() => setVisible(false), 250);
  };

  const storeUrl = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
  const storeLabel = platform === 'ios' ? t('appStore') : t('googlePlay');

  return (
    <div
      role="region"
      aria-label={t('title')}
      className={cn(
        'lg:hidden fixed inset-x-0 bottom-0 z-40',
        'pb-[env(safe-area-inset-bottom)]',
        'transition-transform duration-300 ease-out',
        entered ? 'translate-y-0' : 'translate-y-full',
      )}
    >
      <div className="mx-3 mb-3 rounded-xl bg-stone-950/95 backdrop-blur border border-white/10 shadow-2xl">
        <div className="flex items-center gap-3 p-3">
          <div className="flex-shrink-0 h-11 w-11 rounded-lg bg-dgr-gold/10 border border-dgr-gold/30 flex items-center justify-center overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/dgr/favicon/apple-touch-icon.png"
              alt=""
              className="h-9 w-9 rounded-md"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-semibold leading-tight truncate">
              {t('title')}
            </div>
            <div className="text-white/60 text-xs leading-snug">{t('description')}</div>
          </div>
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center justify-center rounded-md bg-dgr-gold hover:bg-dgr-gold-light text-stone-950 text-xs font-semibold px-3 py-2 transition-colors"
          >
            {storeLabel}
          </a>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('dismiss')}
            className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
